import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Shield, 
  Users, 
  Settings, 
  Database, 
  Activity, 
  UserCheck, 
  AlertTriangle,
  Search,
  Save,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  HelpCircle,
  Trophy
} from 'lucide-react';
import { toast } from 'sonner';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { supabaseUserSearchService } from '@/services/chat/supabase/userSearchService';
import { testingService } from '@/services/testingService';
import { SystemHealthScore } from '@/types/testing';
import { User } from '@/types/chat';
import UserAnalytics from './UserAnalytics';
import SecuritySettings from './SecuritySettings';
import SecurityLogs from './SecurityLogs';
import PlatformSettings from './PlatformSettings';
import SupportCenter from './SupportCenter';
import TopPerformers from './TopPerformers';

const AdminControlPanel: React.FC = () => {
  const { user } = useInternalAuth();
  const [loading, setLoading] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [healthScore, setHealthScore] = useState<SystemHealthScore | null>(null);
  const [systemCheckLoading, setSystemCheckLoading] = useState(false);
  const [currentView, setCurrentView] = useState<'dashboard' | 'analytics' | 'security' | 'logs' | 'settings' | 'support' | 'top-performers'>('dashboard');

  useEffect(() => {
    loadSystemHealth();
  }, []);

  const loadSystemHealth = async () => {
    try {
      const results = await testingService.runComprehensiveTests();
      setHealthScore(results);
    } catch (error) {
      console.error('Error loading system health:', error);
    }
  };

  const handleUserSearch = async (query: string) => {
    setSearchUser(query);
    
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const cleanQuery = query.replace('@', '').trim();
    
    try {
      console.log('Searching for users:', cleanQuery);
      const users = await supabaseUserSearchService.searchUsers(cleanQuery);
      console.log('Search results:', users);
      setSearchResults(users);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setSearchUser(`@${user.username}`);
    setSearchResults([]);
  };

  const handleUserRoleUpdate = async () => {
    if (!selectedUser || !selectedRole) {
      toast.error('Please select a user and role');
      return;
    }

    setLoading(true);
    try {
      toast.success(`User ${selectedUser.username} role updated to ${selectedRole}`);
      setSelectedUser(null);
      setSearchUser('');
      setSelectedRole('');
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const handleSystemCheck = async () => {
    setSystemCheckLoading(true);
    try {
      console.log('🔥 Running comprehensive system health check...');
      const results = await testingService.runComprehensiveTests();
      setHealthScore(results);
      toast.success('System health check completed successfully');
    } catch (error) {
      console.error('Error checking system health:', error);
      toast.error('System health check failed');
    } finally {
      setSystemCheckLoading(false);
    }
  };

  const handleDatabaseTools = () => {
    window.open(`https://supabase.com/dashboard/project/gmmfphwrwhvixldazcmp/sql/new`, '_blank');
  };

  const handleUserAnalytics = () => {
    setCurrentView('analytics');
  };

  const handleSecurityLogs = () => {
    setCurrentView('logs');
  };

  const handlePlatformSettings = () => {
    setCurrentView('settings');
  };

  const handleSecuritySettings = () => {
    setCurrentView('security');
  };

  const handleSupportCenter = () => {
    setCurrentView('support');
  };

  const handleTopPerformers = () => {
    setCurrentView('top-performers');
  };

  const getHealthColor = (score: number) => {
    if (score >= 90) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getHealthIcon = (score: number) => {
    if (score >= 90) return <CheckCircle className="h-4 w-4 text-green-500" />;
    if (score >= 60) return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    return <XCircle className="h-4 w-4 text-red-500" />;
  };

  if (currentView === 'analytics') {
    return <UserAnalytics onBack={() => setCurrentView('dashboard')} />;
  }

  if (currentView === 'security') {
    return <SecuritySettings onBack={() => setCurrentView('dashboard')} />;
  }

  if (currentView === 'logs') {
    return <SecurityLogs onBack={() => setCurrentView('dashboard')} />;
  }

  if (currentView === 'settings') {
    return <PlatformSettings onBack={() => setCurrentView('dashboard')} />;
  }

  if (currentView === 'support') {
    return <SupportCenter onBack={() => setCurrentView('dashboard')} />;
  }

  return (
    <div className="space-y-6">
      {/* Admin Info */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="h-5 w-5 text-red-400" />
            Administrator Control Panel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <p className="text-gray-300">
              <span className="font-medium">Username:</span> {user?.username}
            </p>
            <p className="text-gray-300">
              <span className="font-medium">Role:</span> 
              <Badge className="ml-2 bg-red-500 text-white">Administrator</Badge>
            </p>
            <p className="text-gray-300">
              <span className="font-medium">Last Login:</span> {user?.last_login || 'N/A'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* User Management */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="h-5 w-5 text-sports-green" />
            User Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Label htmlFor="search-user" className="text-gray-300">Search User</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="search-user"
                  value={searchUser}
                  onChange={(e) => handleUserSearch(e.target.value)}
                  placeholder="Enter username (with or without @)"
                  className="bg-gray-700 border-gray-600 text-white pl-10"
                />
                {searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-gray-700 border border-gray-600 rounded-md shadow-lg z-10 max-h-40 overflow-y-auto">
                    {searchResults.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => handleUserSelect(user)}
                        className="px-3 py-2 hover:bg-gray-600 cursor-pointer text-white border-b border-gray-600 last:border-b-0"
                      >
                        <div className="font-medium">@{user.username}</div>
                        <div className="text-sm text-gray-400">
                          {user.first_name} {user.last_name} • {user.role || 'No role'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="new-role" className="text-gray-300">Assign Role</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600 shadow-lg">
                  <SelectItem value="player" className="text-white hover:bg-gray-600 focus:bg-gray-600">Player</SelectItem>
                  <SelectItem value="coach" className="text-white hover:bg-gray-600 focus:bg-gray-600">Coach</SelectItem>
                  <SelectItem value="team-owner" className="text-white hover:bg-gray-600 focus:bg-gray-600">Team Owner</SelectItem>
                  <SelectItem value="venue" className="text-white hover:bg-gray-600 focus:bg-gray-600">Venue Manager</SelectItem>
                  <SelectItem value="creative" className="text-white hover:bg-gray-600 focus:bg-gray-600">Creative</SelectItem>
                  <SelectItem value="support" className="text-white hover:bg-gray-600 focus:bg-gray-600">Support</SelectItem>
                  <SelectItem value="fan" className="text-white hover:bg-gray-600 focus:bg-gray-600">Fan</SelectItem>
                  <SelectItem value="parent" className="text-white hover:bg-gray-600 focus:bg-gray-600">Parent</SelectItem>
                  <SelectItem value="admin" className="text-white hover:bg-gray-600 focus:bg-gray-600">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleUserRoleUpdate}
                disabled={loading || !selectedUser || !selectedRole}
                className="bg-sports-green text-black hover:bg-green-600 w-full"
              >
                <UserCheck className="h-4 w-4 mr-2" />
                Update Role
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Health */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-400" />
            System Health
            {healthScore && (
              <div className="ml-auto">
                <Badge className={`${getHealthColor(healthScore.overall)} bg-transparent border`}>
                  {healthScore.overall}% Health
                </Badge>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {healthScore && (
            <>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium">Overall System Health</span>
                  <span className={`font-bold text-xl ${getHealthColor(healthScore.overall)}`}>
                    {healthScore.overall}%
                  </span>
                </div>
                <Progress value={healthScore.overall} className="h-3" />
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {Object.entries(healthScore.categories).map(([category, score]) => (
                  <div key={category} className="flex items-center justify-between p-2 rounded bg-gray-700">
                    <div className="flex items-center gap-2">
                      {getHealthIcon(score)}
                      <span className="text-sm text-gray-300 capitalize">{category.replace('-', ' ')}</span>
                    </div>
                    <span className={`text-sm font-medium ${getHealthColor(score)}`}>{score}%</span>
                  </div>
                ))}
              </div>
            </>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Database Status:</span>
                <Badge className="bg-green-500 text-white">Online</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Authentication:</span>
                <Badge className="bg-green-500 text-white">Active</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Storage:</span>
                <Badge className="bg-green-500 text-white">Available</Badge>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Active Users:</span>
                <span className="text-white font-medium">247</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Total Users:</span>
                <span className="text-white font-medium">1,523</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Pending Approvals:</span>
                <Button
                  onClick={handleUserAnalytics}
                  variant="ghost"
                  className="text-yellow-400 font-medium hover:bg-gray-700 p-0 h-auto"
                >
                  12 (View Details)
                </Button>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={handleSystemCheck}
              disabled={systemCheckLoading}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${systemCheckLoading ? 'animate-spin' : ''}`} />
              Run System Check
            </Button>
            {healthScore && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Clock className="h-4 w-4" />
                Updated: {new Date(healthScore.lastUpdated).toLocaleString()}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Settings className="h-5 w-5 text-purple-400" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Button
              onClick={handleDatabaseTools}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700 h-20 flex-col gap-2"
            >
              <Database className="h-6 w-6" />
              Database Tools
            </Button>
            <Button
              onClick={handleUserAnalytics}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700 h-20 flex-col gap-2"
            >
              <Users className="h-6 w-6" />
              User Analytics
            </Button>
            <Button
              onClick={handleSecurityLogs}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700 h-20 flex-col gap-2"
            >
              <AlertTriangle className="h-6 w-6" />
              Security Logs
            </Button>
            <Button
              onClick={handlePlatformSettings}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700 h-20 flex-col gap-2"
            >
              <Settings className="h-6 w-6" />
              Platform Settings
            </Button>
            <Button
              onClick={handleSupportCenter}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700 h-20 flex-col gap-2"
            >
              <HelpCircle className="h-6 w-6" />
              Support Center
            </Button>
            <Button
              onClick={handleTopPerformers}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700 h-20 flex-col gap-2"
            >
              <Trophy className="h-6 w-6" />
              Top Performers
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Admin Account Settings */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Settings className="h-5 w-5 text-gray-400" />
            Admin Account Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="admin-username" className="text-gray-300">Username</Label>
              <Input
                id="admin-username"
                value={user?.username || ''}
                disabled
                className="bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed"
              />
              <p className="text-sm text-gray-400 mt-1">Username cannot be changed</p>
            </div>
            <div>
              <Label htmlFor="admin-role" className="text-gray-300">Access Level</Label>
              <Input
                id="admin-role"
                value="Administrator"
                disabled
                className="bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed"
              />
            </div>
          </div>
          
          <div className="pt-4 border-t border-gray-700">
            <p className="text-sm text-gray-400 mb-4">
              Password changes and security settings must be managed through the internal authentication system.
            </p>
            <Button
              onClick={handleSecuritySettings}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <Shield className="h-4 w-4 mr-2" />
              Security Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminControlPanel;
